import React from 'react';
import { restUrl } from '../../env';
import { WaitUntilLoggedIn } from '../Core/LoginManager';
import axios from 'axios';
import Button from '../Utils/Button';
import PlayerBotStatsCard from './PlayerBotStatsCard';
import PlayerBotRoomRow from './PlayerBotRoomRow';
import PlayerBotUserRow from './PlayerBotUserRow';
import ConfirmationPopup from '../Popups/ConfirmationPopup';
import { popup } from '../Utils/Popup';
import '../../css/playerBots.css';

export default class PlayerBots extends React.Component
{
  state = {
    stats: {
      totalPlayerBots: 0,
      activePlayerBots: 0,
      availablePlayerBots: 0,
      awakePlayerBots: 0,
      waitingRooms: 0,
      inProgressRooms: 0
    },
    rooms: [],
    users: [],
    loading: {
      stats: false,
      rooms: false,
      users: false
    },
    lastUpdated: null,
    autoRefresh: true,
    refreshInterval: null,
    searchTerm: '',
    statusFilter: 'all',
    roomStatusFilter: 'all',
    selectedUsernames: [],
    selectedUserIds: [],
    isDeleting: false
  };

  // Flag to prevent duplicate initialization in React Strict Mode
  isInitialized = false;

  async componentDidMount()
  {
    // Prevent duplicate initialization in React Strict Mode
    if( this.isInitialized )
    {
      return;
    }
    this.isInitialized = true;

    await WaitUntilLoggedIn( async () =>
    {
      await this.loadAllData();
      this.startAutoRefresh();
    } );
  }

  componentWillUnmount()
  {
    this.stopAutoRefresh();
  }

  startAutoRefresh = () =>
  {
    if( this.state.autoRefresh && !this.state.refreshInterval )
    {
      const interval = setInterval( () =>
      {
        this.loadAllData( false ); // Silent refresh
      }, 30000 ); // 30 seconds

      this.setState( { refreshInterval: interval } );
    }
  }

  stopAutoRefresh = () =>
  {
    if( this.state.refreshInterval )
    {
      clearInterval( this.state.refreshInterval );
      this.setState( { refreshInterval: null } );
    }
  }

  toggleAutoRefresh = () =>
  {
    const newAutoRefresh = !this.state.autoRefresh;
    this.setState( { autoRefresh: newAutoRefresh } );

    if( newAutoRefresh )
    {
      this.startAutoRefresh();
    }
    else
    {
      this.stopAutoRefresh();
    }
  }

  loadAllData = async ( showLoading = true ) =>
  {
    if( showLoading )
    {
      this.setState( {
        loading: { stats: true, rooms: true, users: true }
      } );
    }

    try
    {
      const [ statsResponse, roomsResponse, usersResponse ] = await Promise.all( [
        axios.post( `${restUrl}/admin/playerBotStats`, {}, { withCredentials: true } ),
        axios.post( `${restUrl}/admin/playerBotRooms`, {}, { withCredentials: true } ),
        axios.post( `${restUrl}/admin/playerBotUsers`, {}, { withCredentials: true } )
      ] );

      const newState = {
        lastUpdated: new Date(),
        loading: { stats: false, rooms: false, users: false }
      };

      if( statsResponse.data.success )
      {
        newState.stats = statsResponse.data.data;
      }

      if( roomsResponse.data.success )
      {
        newState.rooms = roomsResponse.data.data;
      }

      if( usersResponse.data.success )
      {
        console.log( "PlayerBot users response: ", usersResponse.data.data );

        newState.users = usersResponse.data.data;
      }

      this.setState( newState );
    }
    catch( error )
    {
      console.error( 'Failed to load PlayerBot data:', error );
      this.setState( {
        loading: { stats: false, rooms: false, users: false }
      } );
    }
  }

  handleSearchChange = ( event ) =>
  {
    this.setState( { searchTerm: event.target.value } );
  }

  handleStatusFilterChange = ( event ) =>
  {
    this.setState( { statusFilter: event.target.value } );
  }

  handleRoomStatusFilterChange = ( event ) =>
  {
    this.setState( { roomStatusFilter: event.target.value } );
  }

  handleSelectUser = ( username, userId, isSelected ) =>
  {
    this.setState( prevState =>
    {
      const selectedUsernames = isSelected
        ? [ ...prevState.selectedUsernames, username ]
        : prevState.selectedUsernames.filter( uName => uName !== username );

      const selectedUserIds = isSelected
        ? [ ...prevState.selectedUserIds, userId ]
        : prevState.selectedUserIds.filter( id => id !== userId );

      return { selectedUsernames, selectedUserIds };
    } );
  }

  handleSelectAll = ( isSelected ) =>
  {
    this.setState( prevState =>
    {
      const filteredUsers = this.getFilteredUsers();

      const selectedUserIds = isSelected
        ? filteredUsers.map( user => user.userId )
        : [];

      const selectedUsernames = isSelected
        ? filteredUsers.map( user => user.username )
        : [];
      return { selectedUsernames, selectedUserIds };
    } );
  }

  handleDeleteSelected = () =>
  {
    if( this.state.selectedUserIds.length === 0 ) return;

    const selectedUsernames = this.getFilteredUsers()
      .filter( user => this.state.selectedUsernames.includes( user.username ) )
      .map( user => user.username );

    const confirmationMessage = this.state.selectedUsernames.length === 1
      ? `Are you sure you want to delete PlayerBot "${selectedUsernames[ 0 ]}"? This action cannot be undone.`
      : `Are you sure you want to delete ${this.state.selectedUsernames.length} PlayerBots (${selectedUsernames.join( ', ' )})? This action cannot be undone.`;

    popup( <ConfirmationPopup
      title="Delete PlayerBots"
      body={ confirmationMessage }
      buttonText="Delete"
      onConfirm={ this.handleDeleteConfirmation }
    /> );
  }

  handleDeleteConfirmation = async () =>
  {
    try
    {
      this.setState( { isDeleting: true } );

      console.log( "handleDeleteConfirmation - selectedUserIds: ", this.state.selectedUserIds );

      const response = await axios.post( `${restUrl}/admin/deletePlayerBots`, {
        playerBotIds: this.state.selectedUserIds
      }, { withCredentials: true } );

      if( response.data.success )
      {
        // Clear selections and refresh data
        this.setState( {
          selectedUserIds: [],
          isDeleting: false
        } );

        // Reload data to reflect changes
        await this.loadAllData( false );
      }
      else
      {
        console.error( 'Failed to delete PlayerBots:', response.data.error );
        this.setState( { isDeleting: false } );
      }
    }
    catch( error )
    {
      console.error( 'Error deleting PlayerBots:', error );
      this.setState( { isDeleting: false } );
    }
  }

  getFilteredUsers = () =>
  {
    let filtered = [ ...this.state.users ];

    // Search filter
    if( this.state.searchTerm )
    {
      const searchLower = this.state.searchTerm.toLowerCase();
      filtered = filtered.filter( user =>
        user.username.toLowerCase().includes( searchLower )
      );
    }

    // Status filter
    if( this.state.statusFilter !== 'all' )
    {
      filtered = filtered.filter( user => user.status === this.state.statusFilter );
    }

    // Sort: Active first, then Available, then by join date
    filtered.sort( ( a, b ) =>
    {
      const statusOrder = { 'active': 0, 'available': 1, 'awake': 2 };
      const aOrder = statusOrder[ a.status ] ?? 3;
      const bOrder = statusOrder[ b.status ] ?? 3;

      if( aOrder !== bOrder )
      {
        return aOrder - bOrder;
      }

      return new Date( b.joinedDate ) - new Date( a.joinedDate );
    } );

    return filtered;
  }

  getFilteredRooms = () =>
  {
    let filtered = [ ...this.state.rooms ];

    // Room status filter
    if( this.state.roomStatusFilter !== 'all' )
    {
      filtered = filtered.filter( room =>
        this.state.roomStatusFilter === 'waiting' ? !room.started : room.started
      );
    }

    // Sort: Waiting rooms first, then by creation date
    filtered.sort( ( a, b ) =>
    {
      if( a.started !== b.started )
      {
        return a.started ? 1 : -1; // Waiting (not started) first
      }

      return new Date( b.createdAt ) - new Date( a.createdAt );
    } );

    return filtered;
  }

  formatLastUpdated = () =>
  {
    if( !this.state.lastUpdated ) return 'Never';

    const now = new Date();
    const diff = Math.floor( ( now - this.state.lastUpdated ) / 1000 );

    if( diff < 60 ) return `${diff} seconds ago`;
    if( diff < 3600 ) return `${Math.floor( diff / 60 )} minutes ago`;
    return `${Math.floor( diff / 3600 )} hours ago`;
  }

  render()
  {
    const { stats, loading, autoRefresh } = this.state;
    const filteredUsers = this.getFilteredUsers();
    const filteredRooms = this.getFilteredRooms();

    return (
      <div className="playerbots-container">
        <div className="playerbots-header">
          <div className="playerbots-title-section">
            <h4>PlayerBots Dashboard</h4>
            <div className="playerbots-status-info">
              <span className="last-updated">
                Last updated: { this.formatLastUpdated() }
              </span>
              <div className="auto-refresh-control">
                <label>
                  <input
                    type="checkbox"
                    checked={ autoRefresh }
                    onChange={ this.toggleAutoRefresh }
                  />
                  Auto-refresh (30s)
                </label>
              </div>
            </div>
          </div>

          <Button
            className="refresh-button"
            onClick={ () => this.loadAllData() }
            disabled={ loading.stats || loading.rooms || loading.users }
          >
            { loading.stats || loading.rooms || loading.users ? 'Refreshing...' : 'Refresh' }
          </Button>
        </div>

        {/* Statistics Dashboard */ }
        <div className="playerbots-stats-section">
          <h5>Statistics Overview</h5>
          <div className="playerbots-stats-grid">
            <PlayerBotStatsCard
              title="Total PlayerBots"
              value={ stats.totalPlayerBots }
              type="total"
              icon="👥"
              loading={ loading.stats }
            />
            <PlayerBotStatsCard
              title="Active PlayerBots"
              value={ stats.activePlayerBots }
              type="active"
              icon="🟢"
              loading={ loading.stats }
            />
            <PlayerBotStatsCard
              title="Available PlayerBots"
              value={ stats.availablePlayerBots }
              type="available"
              icon="🔵"
              loading={ loading.stats }
            />
            <PlayerBotStatsCard
              title="Awake PlayerBots"
              value={ stats.awakePlayerBots }
              type="awake"
              icon="🟡"
              loading={ loading.stats }
            />
            <PlayerBotStatsCard
              title="Waiting Rooms"
              value={ stats.waitingRooms }
              type="waiting"
              icon="🟠"
              loading={ loading.stats }
            />
            <PlayerBotStatsCard
              title="In-Progress Rooms"
              value={ stats.inProgressRooms }
              type="progress"
              icon="🔴"
              loading={ loading.stats }
            />
          </div>
        </div>

        {/* Rooms with PlayerBots */ }
        <div className="playerbots-rooms-section">
          <div className="section-header">
            <h5>Rooms with PlayerBots ({ filteredRooms.length })</h5>
            <div className="filters">
              <select
                value={ this.state.roomStatusFilter }
                onChange={ this.handleRoomStatusFilterChange }
                className="filter-select"
              >
                <option value="all">All Rooms</option>
                <option value="waiting">Waiting</option>
                <option value="progress">In Progress</option>
              </select>
            </div>
          </div>

          <div className="playerbots-rooms-table">
            <div className="table-header">
              <div className="col-room-id">Room ID</div>
              <div className="col-status">Status</div>
              <div className="col-players">Players</div>
              <div className="col-playerbots">PlayerBots</div>
              <div className="col-speed">Speed</div>
              <div className="col-created">Created</div>
              <div className="col-actions">Actions</div>
            </div>

            <div className="table-body">
              { filteredRooms.length === 0 ? (
                <div className="no-data">
                  { loading.rooms ? 'Loading rooms...' : 'No rooms with PlayerBots found.' }
                </div>
              ) : (
                filteredRooms.map( ( room, index ) => (
                  <PlayerBotRoomRow
                    key={ room.roomId }
                    room={ room }
                    index={ index }
                  />
                ) )
              ) }
            </div>
          </div>
        </div>

        {/* PlayerBot Users */ }
        <div className="playerbots-users-section">
          <div className="section-header">
            <h5>PlayerBot Users ({ filteredUsers.length })</h5>
            <div className="controls-group">
              <div className="selection-controls">
                { this.state.selectedUserIds.length > 0 && (
                  <>
                    <span className="selection-count">
                      { this.state.selectedUserIds.length } selected
                    </span>
                    <Button
                      className="delete-selected-button"
                      onClick={ this.handleDeleteSelected }
                      disabled={ this.state.isDeleting }
                      style={ {
                        backgroundColor: '#dc3545',
                        borderColor: '#dc3545',
                        color: 'white',
                        marginLeft: '10px'
                      } }
                    >
                      { this.state.isDeleting ? 'Deleting...' : `Delete Selected (${this.state.selectedUserIds.length})` }
                    </Button>
                  </>
                ) }
              </div>
              <div className="filters">
                <input
                  type="text"
                  placeholder="Search by username..."
                  value={ this.state.searchTerm }
                  onChange={ this.handleSearchChange }
                  className="search-input"
                />
                <select
                  value={ this.state.statusFilter }
                  onChange={ this.handleStatusFilterChange }
                  className="filter-select"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="available">Available</option>
                  <option value="awake">Awake</option>
                </select>
              </div>
            </div>
          </div>

          <div className="playerbots-users-table">
            <div className="table-header">
              <div className="col-select">
                <input
                  type="checkbox"
                  checked={ filteredUsers.length > 0 && this.state.selectedUserIds.length === filteredUsers.length }
                  onChange={ ( e ) => this.handleSelectAll( e.target.checked ) }
                  title="Select all"
                />
              </div>
              <div className="col-username">Username</div>
              <div className="col-status">Status</div>
              <div className="col-room">Current Room</div>
              <div className="col-timezone">Timezone</div>
              <div className="col-games">Games</div>
              <div className="col-karma">Karma</div>
              <div className="col-joined">Joined</div>
              <div className="col-actions">Actions</div>
            </div>

            <div className="table-body">
              { filteredUsers.length === 0 ? (
                <div className="no-data">
                  { loading.users ? 'Loading users...' : 'No PlayerBot users found.' }
                </div>
              ) : (
                filteredUsers.map( ( user, index ) => (
                  <PlayerBotUserRow
                    key={ user.username }
                    user={ user }
                    index={ index }
                    isSelected={ this.state.selectedUserIds.includes( user.userId ) }
                    onSelect={ this.handleSelectUser }
                  />
                ) )
              ) }
            </div>
          </div>
        </div>
      </div>
    );
  }
}
